import { useState, useRef } from 'react';
import axios from 'axios';
import { UploadSimple, X, FilePdf, Image as ImageIcon } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentProofUpload = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [filename, setFilename] = useState('');
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError('');
    const allowed = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowed.includes(file.type)) { setError('Use JPG, PNG or PDF'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('File must be under 5MB'); return; }

    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/upload/payment-proof`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onChange(data.url);
      setFilename(file.name);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const isPdf = value && (value.endsWith('.pdf') || filename.endsWith('.pdf'));

  return (
    <div>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,application/pdf"
        onChange={onFile}
        className="hidden"
        data-testid="payment-proof-file-input"
      />
      {value ? (
        <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50 p-3">
          <div className="flex items-center gap-3">
            {isPdf ? (
              <FilePdf size={28} weight="duotone" className="text-red-500 flex-shrink-0" />
            ) : (
              <img src={value} alt="Payment screenshot" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" data-testid="payment-proof-preview" />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 truncate">Payment proof uploaded</p>
              <a
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#F97316] hover:underline"
              >
                View file
              </a>
            </div>
            <button
              type="button"
              onClick={() => { onChange(''); setFilename(''); }}
              className="bg-white hover:bg-red-500 hover:text-white text-gray-700 p-2 rounded-lg transition-colors"
              data-testid="payment-proof-clear-btn"
            >
              <X size={14} weight="bold" />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="w-full bg-gray-50 hover:bg-orange-50 border-2 border-dashed border-gray-200 hover:border-[#F97316] text-gray-500 hover:text-[#F97316] rounded-xl p-6 transition-colors flex flex-col items-center justify-center gap-2"
          data-testid="payment-proof-upload-btn"
        >
          <div className="flex items-center gap-2">
            <ImageIcon size={20} weight="bold" />
            <FilePdf size={20} weight="bold" />
          </div>
          <span className="text-sm font-semibold">{uploading ? 'Uploading...' : 'Upload Payment Screenshot'}</span>
          <span className="text-xs text-gray-400">JPG, PNG or PDF — max 5MB</span>
        </button>
      )}
      {error && <p className="text-red-600 text-xs mt-2" data-testid="payment-proof-error">{error}</p>}
    </div>
  );
};

export default PaymentProofUpload;
